import { get } from 'svelte/store';

// import { format } from 'svelte-i18n';
import CacheStatisticRequest from '../components/Chart/chart_cache_helper';

import { user_data, user_settings } from 'src/stores/account/user';
import { firmwares_gen_2 } from 'src/stores/device-runtime/process';
import { app, os } from 'src/stores/session/native-bridge';

import { api, clearToken, standardLogin } from '../services/http';
import { defaultAppBackgroundSrc } from './constants';

import {
	logout_all_android,
	logout_mobile_android,
} from '../services/native_container_requests_android';
import { logout_all_ios, logout_mobile_ios } from '../services/native_container_requests_ios';

import { load_image_from } from '../helpers/interface_helpers';
import { profiles } from 'src/stores/features/profiles';
import { setUpI18n } from 'src/services/i18n';
import { socket_disconnect } from 'src/services/socket';
import { handleUserProfile, removeProfile } from './login_helpers';

import type { MenuListDataType } from 'src/components/Profiles/DropdownItem.types';
import type { AvailableSubUnits, AvailableUnitsSelection } from 'src/types/responses/usersAuthResponse';
import { isMultiUnitResult } from 'src/services/auth_v2';
import { toast } from '@Shelly/shelly-design-system/index';
import { replace } from 'svelte-spa-router';
import { stopAndClearNotifications } from './notifications/device_notifications_helper';
import { resetDrawer } from './drawer_helpers';
import { handleLegacyLoginErrors } from 'src/services/php-api/legacy/v1_auth';
import {
	handleAuthErrorMessage,
	shouldForceLogout,
	type AuthErroMessage,
} from 'src/services/auth_errors';
import { compare } from 'compare-versions';
import { clearTokenWatch } from 'src/services/auth_token';
import type { UserData } from 'src/types/UserData';
import { clearCameraStores } from 'src/components/Camera/helpers/camera-helpers';
import { resetSubUnitsCache } from 'src/components/Scenes/helpers/notification_recipients_helpers';
import { aiSceneChatStore } from 'src/stores/features/ai-scene-chat';
import { resetAllStores } from 'src/stores/reset';
import { clearHomes, clearPrimedUnitsInfo } from 'src/stores/account/home';
import { clearUnitTokenCache, invalidateAccountSessions } from 'src/services/unit_token_cache';

/**
 * Clears user-related data on login
 */
export const clearDataOnLogin = () => {
	//@ToDo: Review this function; Moved here from standardLogin
	CacheStatisticRequest.clear();
	firmwares_gen_2.reset();
	clearCameraStores();
	// Profile-switching path: the new account's sub-units are scoped to its
	// own home, so any cached entries from the previous account must be
	// dropped before the wizard or side-panel queries them.
	resetSubUnitsCache();

	resetDrawer('login');
	aiSceneChatStore.clearAllData();
};

export const clearAllData = () => {
	clearTokenWatch();
	clearToken();
	resetAllStores();
	sessionStorage.clear();
	CacheStatisticRequest.clear();
	aiSceneChatStore.clearAllData();

	clearCameraStores();
	clearHomes();
	clearPrimedUnitsInfo();
	resetSubUnitsCache();
	clearUnitTokenCache();
};

export async function handleLogout(
	selectedProfile?: void | MenuListDataType,
	logoutAllProfiles?: boolean,
) {
	const email = get(user_data).email;
	const token = user_data.getToken();

	//Disconnect socket
	socket_disconnect();

	// loading.open(_format('logging out'), _format('loading_title_please_wait'));
	await api.post(
		'/auth/logout',
		{},
		{
			headers: {
				Authorization: `Bearer ${token}`,
			},
		},
	);

	clearToken();

	// Drop the logged-out account's cached unit tokens so its homes can't be
	// re-served from cache when switching to another profile (which skips
	// `clearAllData()`).
	invalidateAccountSessions(email);

	let backwardsCompatibilityLogoutAll = false;
	if (get(app)) {
		const regex = /Shelly.*?[-\/](\d+\.\d+\.\d+)/;
		const match = navigator.userAgent.match(regex);
		const nativeVersion = match ? match[1] : '0.0.1';
		if (get(os) === 'IOS') {
			logoutAllProfiles
				? compare(nativeVersion, '1.7.0', '>')
					? logout_all_ios() // iOS - it will be available with 1.7.1 and above
					: (backwardsCompatibilityLogoutAll = true)
				: logout_mobile_ios(email.toLowerCase());
		} else {
			logoutAllProfiles
				? compare(nativeVersion, '1.30.4', '>') && +nativeVersion.split('.')[1] > 30 // second number represents the real Android version, so we need also this check
					? logout_all_android() // Android - it will be available with 1.30.5 and above
					: (backwardsCompatibilityLogoutAll = true)
				: logout_mobile_android(email.toLowerCase());
		}
	}

	// Stop and clear notifications on every logout
	stopAndClearNotifications();

	if (logoutAllProfiles) {
		get(profiles).forEach((profile) => {
			if (backwardsCompatibilityLogoutAll) {
				if (get(os) === 'IOS') {
					logout_mobile_ios(profile.email.toLowerCase());
				} else {
					logout_mobile_android(profile.email.toLowerCase());
				}
			}
			removeProfile(profile.email);
		});

		clearAllData();
		return;
	}

	const activeProfile = get(profiles).find((p) => p.email === email);
	const nextProfile: void | MenuListDataType =
		typeof activeProfile !== 'undefined' ? removeProfile(activeProfile.email) : undefined;

	if (typeof nextProfile !== 'undefined' && typeof selectedProfile === 'undefined') {
		return await switchingProfile(nextProfile);
	} else if (typeof selectedProfile !== 'undefined') {
		return await switchingProfile(selectedProfile);
	} else {
		clearAllData();
		replace('/login');
	}
}

/**
 * Handles the logout process for a specific user profile.
 *
 * @param userData User data object containing token and email
 * @returns {Promise<void>} - A promise that resolves when the logout is complete
 */
export const handleSingleProfileLogout = async (userData: UserData): Promise<void> => {
	await api.post(
		'/auth/logout',
		{},
		{
			headers: {
				Authorization: `Bearer ${userData?.token}`,
			},
		},
	);

	const email = userData?.email?.toLowerCase();
	removeProfile(email);

	if (get(app)) {
		if (get(os) === 'IOS') {
			logout_mobile_ios(email);
		} else {
			logout_mobile_android(email);
		}
	}
};

export type PendingUnitSelection = {
	pendingUnitSelection: true;
	profileId: MenuListDataType['id'];
	profileSub: MenuListDataType['sub'];
	availableSubUnits: AvailableSubUnits;
} & AvailableUnitsSelection;

/**
 * Function for switching profile to next available.
 *  Handles switching profile after logout.
 * * @param {MenuListDataType} profile - The profile to switch to.
 * * @returns {Promise<PendingUnitSelection | void>} - Multi-unit info if the user needs to pick a home, void otherwise.
 */
const switchingProfile = async (profile: MenuListDataType): Promise<PendingUnitSelection | void> => {
	let res = await standardLogin(profile.email, profile.pass);

	if (res?.isok && isMultiUnitResult(res)) {
		const ownedHomes = Object.entries(res.availableUnits ?? {});
		const sharedHomes = Object.entries(res.availableSubUnits ?? {});
		const totalAvailableHomes = ownedHomes.length + sharedHomes.length;
		const knownAvailableUnits = res.availableUnits;
		const knownAvailableSubUnits = res.availableSubUnits ?? {};
		const hasTokens = res.accessToken && res.refreshToken;

		if (totalAvailableHomes > 1) {
			return {
				pendingUnitSelection: true,
				profileId: profile.id,
				profileSub: profile.sub,
				availableUnits: knownAvailableUnits,
				availableSubUnits: knownAvailableSubUnits,
				// Discovery token for the target account so the picker can render
				// from its own `get_units_info` (account-scoped, no global pollution).
				discoveryToken: res.data?.token,
				discoveryApiUrl: res.data?.user_api_url,
			};
		}

		if (!hasTokens) {
			let selectedHomeId: string | undefined;
			let sharedHomeId: string | undefined;

			if (ownedHomes.length === 1) {
				selectedHomeId = ownedHomes[0][0];
			} else {
				const [homeId, homeValue] = sharedHomes[0];
				selectedHomeId = homeId;
				sharedHomeId =
					typeof homeValue === 'object' ? String((homeValue as any).sub) : undefined;
			}

			res = await standardLogin(profile.email, profile.pass, selectedHomeId, sharedHomeId);
		}
	}

	if (res?.isok) {
		clearDataOnLogin();
		setUpI18n(res.data.lang);
		await handleUserProfile(profile.id);
	} else {
		const alertProps = handleLoginErrors(res);
		toast.open({
			type: 'danger',
			title: alertProps.heading,
			text: alertProps.message,
		});

		// Handle invalid session error and remove profile
		// E.g. wrong credentials, too many attempts, etc.
		if (shouldForceLogout(res as Parameters<typeof shouldForceLogout>[0])) {
			profiles.remove(profile.email);
			if (get(profiles).length <= 0) {
				// Reset profiles and redirect to login if no profiles remain
				console.warn('[AUTH] No profiles left after logout, resetting profiles store');
				profiles.reset();
				clearAllData();
				replace('/login');
			} else {
				// Switching to the next available profile
				await switchingProfile(get(profiles)[0]);
			}
		}
	}
};

export function getAppBackgroundSrc(homeData: any, useLoadImg = false) {
	// If backgroundColor is set, return empty string for the image path
	if (homeData?.layout?.backgroundColor) {
		return '';
	}

	const appBackgroud = homeData?.layout?.appBackgroud || defaultAppBackgroundSrc;
	return useLoadImg ? load_image_from(appBackgroud) : appBackgroud;
}

export function applicationHostUrl(url: string = undefined, route: string = undefined) {
	// this is used in cases like forgotten password to generate some source urls as reset password button's address from email address
	// if (get(app)) {
	// 	url = 'https://control.shelly.cloud'; //hardcoded until we provide a proper way to get src from containers
	// }
	return `${url.includes('http') ? url : 'https://' + url}` + `${route ? route : ''}`;
}

/**
 * Handle Login Errors - PHP API Legacy login
 * @deprecated : Deprecate after full migration to iotu v2 login
 * - Use `handleLegacyLoginErrors` for the PHP-API login services.
 * - Use auth_error `handleAuthError` for generic auth errors.
 *
 * This function processes the errors received during the login attempt and returns an object with alert properties.
 * It uses the legacy login error handler for compatibility with existing code.
 * @param response Api response object containing `errors` or `error` object
 * @param email
 * @returns  {AuthErroMessage}	Alert props
 */
export function handleLoginErrors(response: any, email?: string): AuthErroMessage {
	// Legace PHP API login error handling
	if (response?.errors) {
		return handleLegacyLoginErrors(response?.errors, email);
	}
	// Handle auth errors
	return handleAuthErrorMessage(response, email);
}

export function isSupportAccessExpired(supportAccessExpiryTimestamp: number): boolean {
	return (
		Number.isFinite(supportAccessExpiryTimestamp) &&
		supportAccessExpiryTimestamp < new Date().getTime()
	);
}
